import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { adminApi } from '@/api/adminApi'
import type { AdminMetricsSnapshot } from '@/api/types'
import { AdminCard, AdminPageHeader, Money } from '@/components/admin/AdminUi'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'

const planNames: Record<number, string> = { 1: 'Free', 2: 'Pro', 3: 'Team' }
const statusNames: Record<number, string> = { 1: 'Gratuito', 2: 'Ativo', 3: 'Trial', 4: 'Cancelado', 5: 'Em atraso' }

export default function MetricsPage() {
  const { t } = useTranslation()
  const [data, setData] = useState<AdminMetricsSnapshot | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { adminApi.metrics().then(setData).catch((err: Error) => setError(err.message)) }, [])
  if (error) return <EmptyState title={t('common.errorLoading', 'Erro ao carregar')} description={error} />
  if (!data) return <div className="grid gap-3 md:grid-cols-3"><Skeleton height={120} /><Skeleton height={120} /><Skeleton height={120} /></div>

  const activeSubscriptions = Object.values(data.by_plan).reduce((total, value) => total + value.subscriptions, 0)
  const cards: Array<[string, ReactNode, string?]> = [
    ['Usuários', data.total_users, `${data.active_users} ativos`],
    ['Workspaces', data.total_workspaces, `${data.active_workspaces} ativos`],
    ['Assinaturas ativas', activeSubscriptions],
    [t('admin.metrics.grossMrr', 'MRR bruto'), <Money cents={data.gross_mrr_cents} />],
    ['MRR líquido', data.net_mrr_cents === null ? 'Indisponível' : <Money cents={data.net_mrr_cents} />, data.net_mrr_status === 'complete' ? 'Cálculo completo' : 'Não foi possível consultar todos os descontos'],
    [t('admin.metrics.atRisk', 'MRR em risco'), <Money cents={data.mrr_at_risk_cents} />],
    ['Receita paga no período', <Money cents={data.paid_revenue_cents} />],
    [t('admin.metrics.newPaying', 'Novos pagantes'), data.new_paying_workspaces],
    [t('admin.metrics.incomplete', 'Contratos incompletos'), data.incomplete_contracts],
  ]

  return (
    <div>
      <AdminPageHeader
        title={t('admin.metrics.title', 'Métricas')}
        description={`${new Date(data.period.start).toLocaleDateString('pt-BR')} a ${new Date(data.period.end).toLocaleDateString('pt-BR')} · receita, base, aquisição, distribuição e funil.`}
      />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map(([label, value, detail]) => (
          <AdminCard key={String(label)}>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{label}</div>
            <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
            {detail ? <div className="mt-1 text-[11px]" style={{ color: 'var(--text-dim)' }}>{detail}</div> : null}
          </AdminCard>
        ))}
      </div>
      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <AdminCard>
          <h2 className="mb-3 text-sm font-semibold">{t('admin.metrics.byPlan', 'MRR por plano')}</h2>
          {Object.keys(data.by_plan).length === 0 ? <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Nenhuma assinatura paga ativa.</p> : Object.entries(data.by_plan).map(([plan, value]) => (
            <div key={plan} className="flex justify-between border-t py-2 text-xs" style={{ borderColor: 'var(--border)' }}>
              <span className="uppercase">{plan} ({value.subscriptions})</span><strong><Money cents={value.mrr_cents} /></strong>
            </div>
          ))}
        </AdminCard>
        <AdminCard>
          <h2 className="mb-3 text-sm font-semibold">Novos workspaces por semana</h2>
          {data.new_workspaces_by_week.length === 0 ? <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Nenhum workspace criado no período.</p> : data.new_workspaces_by_week.map((row) => (
            <div key={row.week} className="flex justify-between border-t py-2 text-xs" style={{ borderColor: 'var(--border)' }}>
              <span>Semana de {new Date(row.week).toLocaleDateString('pt-BR')}</span><strong>{row.count}</strong>
            </div>
          ))}
        </AdminCard>
        <AdminCard>
          <h2 className="mb-3 text-sm font-semibold">Distribuição das assinaturas</h2>
          {data.distribution.map((row) => (
            <div key={`${row.plan__code}-${row.status}`} className="flex justify-between border-t py-2 text-xs" style={{ borderColor: 'var(--border)' }}>
              <span>{planNames[row.plan__code] ?? row.plan__code} · {statusNames[row.status] ?? row.status}</span><strong>{row.count}</strong>
            </div>
          ))}
        </AdminCard>
        <AdminCard>
          <h2 className="mb-3 text-sm font-semibold">{t('admin.metrics.funnel', 'Funil')}</h2>
          {Object.entries(data.funnel).map(([step, count]) => (
            <div key={step} className="mb-3">
              <div className="flex justify-between text-xs"><span>{t(`admin.funnel.${step}`, step)}</span><strong>{count}</strong></div>
              <div className="mt-1 h-2 rounded" style={{ background: 'var(--bg-toolbar)' }}><div className="h-2 rounded" style={{ width: `${data.funnel.registered ? Math.round(count / data.funnel.registered * 100) : 0}%`, background: 'var(--accent)' }} /></div>
            </div>
          ))}
        </AdminCard>
      </div>
    </div>
  )
}
